import React, { useState, useEffect } from 'react';
import BackToHomeButton from './BackToHomeButton';
// No need to import ScrollReveal here, it's loaded via script
import './AboutUs.css';

// Import hero and content images
import heroImage from '../assets/environment/qualitylabs.png'; 
import storyImage from '../assets/environment/mainoffice.png'; 

// Import gallery images
import galleryenterence from '../assets/environment/Board.png';
import galleryImage1 from '../assets/environment/garden.png';
import galleryImage2 from '../assets/environment/mainoffice.png';
import galleryImage3 from '../assets/environment/manufacturingunit.png';
import galleryImage4 from '../assets/environment/phosphatingsection.png';
import galleryImage5 from '../assets/environment/qualitylabs.png';
import galleryImage6 from '../assets/environment/saraswathi.png';
import galleryImage7 from '../assets/environment/storage.png';
import galleryImage8 from '../assets/environment/coldforging.png';

const galleryImages = [
  galleryenterence,
  galleryImage1,
  galleryImage2,
  galleryImage3,
  galleryImage4,
  galleryImage5,
  galleryImage6,
  galleryImage7,
  galleryImage8,
];

const AboutUs = () => {
  const [isImageMaximized, setIsImageMaximized] = useState(false);
  const [maximizedImage, setMaximizedImage] = useState('');
  const [scrollRevealLoaded, setScrollRevealLoaded] = useState(false);

  const handleImageClick = (image) => {
    setMaximizedImage(image);
    setIsImageMaximized(true);
  };

  const handleCloseImage = () => {
    setIsImageMaximized(false);
    setMaximizedImage('');
  };

  // Effect to dynamically load the ScrollReveal script
  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://unpkg.com/scrollreveal@4.0.9/dist/scrollreveal.min.js';
    script.async = true;
    script.onload = () => setScrollRevealLoaded(true);
    document.body.appendChild(script);
    return () => {
      document.body.removeChild(script);
    };
  }, []);

  // ScrollReveal animations
  useEffect(() => {
    if (scrollRevealLoaded && window.ScrollReveal) {
      const sr = window.ScrollReveal();
      sr.reveal('.reveal', {
        origin: 'bottom',
        distance: '60px',
        duration: 1200,
        easing: 'cubic-bezier(0.5, 0, 0, 1)',
        interval: 150,
        reset: false,
      });
    }
  }, [scrollRevealLoaded]);

  // Close modal with Escape key
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        handleCloseImage();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  return (
    <>
      {/* Styles for gallery + modal */}
      <style>{`
        .gallery {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
          gap: 1.5rem;
          padding: 0 1rem;
        }
        .gallery-item img {
          width: 100%;
          height: 224px; /* Matches h-56 from Tailwind */
          object-fit: cover;
          border-radius: 0.5rem;
          cursor: pointer;
          transition: transform 0.3s ease, box-shadow 0.3s ease;
        }
        .gallery-item img:hover {
          transform: scale(1.05);
          box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1),
                      0 4px 6px -2px rgba(0, 0, 0, 0.05);
        }
        .maximized-image-container {
          position: fixed; 
          top: 0; left: 0; 
          width: 100%; height: 100%; 
          z-index: 50;
          display: flex; 
          align-items: center; 
          justify-content: center; 
          padding: 1rem;
          overflow: auto; /* ✅ modal scroll enabled */
        }
        .maximized-image-overlay {
          position: absolute; 
          top: 0; left: 0; 
          width: 100%; height: 100%;
          background-color: rgba(0, 0, 0, 0.8);
        }
        .maximized-image {
          position: relative; 
          z-index: 51; 
          max-width: 95%; 
          max-height: none;
        }
        .maximized-image img {
          display: block;
          width: 100%;   /* ✅ responsive on mobile */
          height: auto;  /* ✅ maintain aspect ratio */
          border-radius: 0.5rem;
        }
        .close-button {
          position: absolute; 
          top: -1rem; 
          right: -1rem; 
          background: white; 
          color: black;
          border: none; 
          border-radius: 9999px; 
          width: 2.5rem; 
          height: 2.5rem;
          font-size: 1.5rem; 
          font-weight: bold; 
          cursor: pointer; 
          display: flex;
          align-items: center; 
          justify-content: center; 
          box-shadow: 0 4px 6px rgba(0,0,0,0.1);
        }
      `}</style>
      
      <div className="bg-gray-50 font-sans text-gray-800">
        <div className='absolute top-5 left-5 z-20'>
          <BackToHomeButton />
        </div>

        {/* HERO SECTION */}
        <header className="relative min-h-[50vh] md:min-h-[60vh] bg-cover bg-center text-white flex items-center justify-center bg-cover" style={{ backgroundImage: `url(${heroImage})` }}>
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/50 to-transparent"></div>
          <div className="relative z-10 text-center px-4">
            <h1 className="text-4xl md:text-6xl font-extrabold tracking-tight drop-shadow-lg">
              About Gowrihouse Metal Works
            </h1>
            <p className="mt-4 text-lg md:text-xl max-w-3xl mx-auto text-gray-200 drop-shadow-md">
              A Legacy of Quality and Innovation in Fasteners Since 1952
            </p>
          </div>
        </header>
        
        <main className="container mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24 space-y-24">
          
          {/* OUR STORY */}
          <section className="reveal grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div className="lg:pr-8">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">About Us</h2>
              <div className="w-24 h-1.5 bg-blue-600 mb-6"></div>
              <div className="text-gray-600 text-lg leading-relaxed space-y-4">
                <p className='text-justify'>
                  Gowrihouse Metal Works LLP is a leading manufacturer, exporter and supplier of Self-Tapping Screws and Wood Screws, in India. Established in 1952 by the visionary Shri P.A.C. Ramasamy Raja, Founder of the Ramco Group of Industries, our journey began with a commitment to manufacturing Mild Steel Woodscrews and achieving complete consumer satisfaction.
                </p>
                <p className='text-justify'>
                  In 2018, we transitioned into a Limited Liability Partnership (LLP) to expand our business, enhance management, and better serve our customers. Today, our manufacturing range includes Self-Tapping Screws, Mirror Screws, Drywall Screws, Concrete Nails, and Self-Drilling Screws.
                </p>
                <p className='text-justify'>
                  Our unit is professionally managed, equipped with state-of-the-art machinery, and operates with a strong commitment to environmental standards, quality norms, and modern management systems like 5S, Kanban, and Kaizen.
                </p>
              </div>
            </div>
            <div className="flex items-center justify-center">
              <img src={storyImage} alt="Gowrihouse Metal Works Office" className="rounded-xl shadow-2xl w-full h-auto object-cover max-h-[500px]" />
            </div>
          </section>

          {/* OUR VISION */}
          <section className="reveal">
            <div className="bg-white rounded-2xl shadow-xl overflow-hidden p-8 md:p-12 lg:p-16 max-w-5xl mx-auto border-t-8 border-blue-600">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 text-center">Our Vision</h2>
              <div className="w-24 h-1.5 bg-blue-600 mb-8 mx-auto"></div>
              <p className="text-gray-600 leading-relaxed text-center text-xl md:text-2xl font-light">
                To be the top fastener manufacturer through <strong className="font-semibold text-gray-800">quality</strong>, <strong className="font-semibold text-gray-800">innovation</strong>, and <strong className="font-semibold text-gray-800">know-how</strong>. We strive to provide the right solutions for our customers, be recognized internationally, and form synergistic, long-term profitable relationships with our partners and customers.
              </p>
            </div>
          </section>
          
          {/* GALLERY */}
          <div className="pt-12">
            <h2 className="reveal text-3xl md:text-4xl font-bold text-center text-gray-800 mb-4">Our Gallery</h2>
            <div className="reveal w-24 h-1.5 bg-blue-600 mb-12 mx-auto"></div>
            <div className="gallery">
              {galleryImages.map((image, index) => (
                <div key={index} className="gallery-item reveal">
                  <img src={image} alt={`Gallery ${index + 1}`} onClick={() => handleImageClick(image)} />
                </div>
              ))}
            </div>
          </div>
        </main>

        {/* IMAGE MODAL */}
        {isImageMaximized && (
          <div className="maximized-image-container">
            <div className="maximized-image-overlay" onClick={handleCloseImage}></div>
            <div className="maximized-image">
              <img src={maximizedImage} alt="Maximized View" />
              <button className="close-button" onClick={handleCloseImage}>&times;</button>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default AboutUs;
